import React from "react";
import { Context } from "../context/Context";
import "./AppErrorBoundary.css";

// Last line of defence for unexpected RENDERING failures (a component that throws, a lazy page whose chunk failed to
// load). API errors never reach it: they are values handled by the page, utils/errors and ApiNoticeHost.
// The fallback never shows the error or its stack; the console keeps it for developers.
const TEXT = {
  en: {
    title: "Something went wrong on this page",
    body: "An unexpected problem stopped this page from showing. Your data was not changed.",
    chunk: "A new version of the application may be available. Reloading usually fixes this.",
    retry: "Try again",
    reload: "Reload page",
  },
  ar: {
    title: "حدث خطأ في هذه الصفحة",
    body: "منعت مشكلة غير متوقعة عرض هذه الصفحة. لم تتغير بياناتك.",
    chunk: "قد يتوفر إصدار جديد من التطبيق. إعادة تحميل الصفحة تحل هذا عادةً.",
    retry: "حاول مرة أخرى",
    reload: "إعادة تحميل الصفحة",
  },
};

const isChunkError = (error) => /ChunkLoadError|Loading (CSS )?chunk|dynamically imported module/i.test(`${error?.name} ${error?.message}`);

export class AppErrorBoundary extends React.Component {
  static contextType = Context;

  constructor(props) {
    super(props);
    this.state = { error: null };
    this.headingRef = React.createRef();
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Developer diagnostics only (no user data is logged here).
    console.error("[AppErrorBoundary]", error, info?.componentStack);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.error && this.state.error && this.headingRef.current) this.headingRef.current.focus();
    // A different page (resetKey = location) gets a fresh chance.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const lang = (this.context && this.context.lang) || this.props.lang || "ar";
    const text = TEXT[lang] || TEXT.ar;
    return (
      <div className="app-error-boundary" role="alert" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang} data-testid="app-error-boundary">
        <div className="app-error-boundary__card">
          <h1 tabIndex={-1} ref={this.headingRef} className="app-error-boundary__title">
            {text.title}
          </h1>
          <p>{isChunkError(error) ? text.chunk : text.body}</p>
          <div className="app-error-boundary__actions">
            {!isChunkError(error) ? (
              <button type="button" className="app-error-boundary__button" onClick={() => this.setState({ error: null })}>
                {text.retry}
              </button>
            ) : null}
            <button
              type="button"
              className="app-error-boundary__button app-error-boundary__button--primary"
              onClick={() => window.location.reload()}
            >
              {text.reload}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
